import React, { useState, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaPlus } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import { getEnabledPackages, getAllowedTabNames, getAllowedTabIds } from '../../utils/entitlements';
import { theme } from '../../theme/theme';
import './css/Menu.css';

const titleCase = (str) =>
  (str || '').replace(/\w\S*/g, (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());

const Menu = ({ selectedAppId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTabs, setActiveTabs] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [moreHoveredGroupKey, setMoreHoveredGroupKey] = useState(null);

  useEffect(() => {
    fetchActiveTabs();
  }, []);

  useEffect(() => {
    if (!selectedAppId) {
      setSelectedApp(null);
      return;
    }
    const fetchApp = async () => {
      try {
        const appsApi = new DataApi('apps');
        const res = await appsApi.fetchById(selectedAppId);
        if (res?.data?.success && res.data.data) setSelectedApp(res.data.data);
        else setSelectedApp(null);
      } catch (err) {
        console.error('Error fetching selected app for menu:', err);
        setSelectedApp(null);
      }
    };
    fetchApp();
  }, [selectedAppId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (openDropdownId !== null) {
      const handleClickOutside = (event) => {
        // Check if click is outside the dropdown
        const dropdownElement = document.querySelector(`[data-tab-dropdown-id="${openDropdownId}"]`);
        if (dropdownElement && !dropdownElement.contains(event.target)) {
          setOpenDropdownId(null);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdownId]);

  const fetchActiveTabs = async () => {
    try {
      setLoadingTabs(true);
      const tabApi = new DataApi('tabs');
      // Fetch tabs for web platform (use 'platform=mobile' for mobile app)
      const response = await tabApi.fetchAll('is_active=true&platform=web');
      console.log("## response: ", response.data.data);
      if (response?.data?.success) {
        // Sort tabs by order_no (already sorted by backend, but keeping for safety)
        const sortedTabs = response.data.data.sort(
          (a, b) => a.order_no - b.order_no
        );
        setActiveTabs(sortedTabs);
      } else {
        setActiveTabs([]);
      }
    } catch (error) {
      console.error('Error fetching active tabs:', error);
      setActiveTabs([]);
    } finally {
      setLoadingTabs(false);
    }
  };

  const normalizePath = path => {
    if (!path) {
      return '';
    }
    if (path === '/') {
      return '/';
    }
    const trimmed = path.replace(/\/+$/, '');
    return trimmed.toLowerCase();
  };

  const currentPath = normalizePath(location.pathname);

  const doesPathMatch = basePath => {
    if (!basePath) return false;
    if (currentPath === basePath) {
      return true;
    }
    return currentPath.startsWith(`${basePath}/`);
  };

  const isTabActive = tab => {
    if (!tab) return false;

    const tabUrl = normalizePath(tab.url);
    if (tabUrl && doesPathMatch(tabUrl)) {
      return true;
    }

    const objectName = tab.object_name?.toLowerCase();
    if (objectName) {
      const objectPaths = ['/listview', '/view', '/edit'].map(
        prefix => `${prefix}/${objectName}`
      );

      if (objectPaths.some(path => doesPathMatch(path))) {
        return true;
      }
    }

    return false;
  };

  if (loadingTabs) {
    return (
      <div className='d-flex align-items-center'>
        <div
          className='spinner-border spinner-border-sm text-light me-2'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
        <span className='text-light'>Loading...</span>
      </div>
    );
  }

  // Resolve group: app override (when tab came from selected app) then config/top-level
  const getGroupKey = (t) => (t.appGroupKey ?? t.config?.group_key ?? t.group_key)?.trim();
  const getGroupLabel = (t) => (t.appGroupLabel ?? t.config?.group_label ?? t.group_label)?.trim();
  const getTabPackage = (t) => (t.config?.package ?? t.package)?.trim?.() || '';

  const enabledPackages = getEnabledPackages();
  const normalizeTabRef = (s) => (s || '').toString().toLowerCase().trim();
  const allowedTabIds = getAllowedTabIds();
  const allowedIdSet = allowedTabIds.length > 0 ? new Set(allowedTabIds.map(id => String(id))) : null;
  const allowedTabNames = getAllowedTabNames();
  const allowedNameSet = allowedTabNames.length > 0 ? new Set(allowedTabNames) : null;
  const normalizeName = (s) => {
    const x = (s || '').toString().toLowerCase().trim();
    return x === 'calender' ? 'calendar' : x;
  };

  // Build base list: either from selected app (order + groups from app config) or default (all tabs)
  let visibleTabs;
  if (selectedApp?.config && (selectedApp.config.tabs?.length > 0 || selectedApp.config.tabIds?.length > 0)) {
    const appTabsConfig = selectedApp.config.tabs?.length
      ? selectedApp.config.tabs
      : (selectedApp.config.tabIds || []).map((id) => ({ id }));
    const tabById = new Map(activeTabs.map((t) => [String(t.id), t]));
    const tabByName = new Map(activeTabs.map((t) => [(t?.name || '').toString().toLowerCase().trim(), t]));
    visibleTabs = appTabsConfig
      .map((entry) => {
        const tab = entry.id != null ? tabById.get(String(entry.id)) : null
          || (entry.name != null ? tabByName.get((entry.name || '').toString().toLowerCase().trim()) : null);
        if (!tab) return null;
        return {
          ...tab,
          appGroupKey: entry.group_key?.trim() || undefined,
          appGroupLabel: entry.group_label?.trim() || undefined,
        };
      })
      .filter(Boolean);
  } else {
    visibleTabs = activeTabs.filter((t) => {
      const pkg = getTabPackage(t);
      return !pkg || enabledPackages.includes(pkg);
    });
    visibleTabs = visibleTabs.filter((t) => {
      const obj = normalizeTabRef(t?.object_name);
      const name = normalizeTabRef(t?.name);
      if (obj === 'user' || name === 'user') return false;
      if (obj === 'mail_config' || name === 'mail_config' || name === 'mailconfig') return false;
      if (obj === 'email_template' || name === 'email_template' || name === 'emailtemplate') return false;
      return true;
    });
  }

  // Common filters: User/Mail/EmailTemplate (always), package (when tabs from app), license (always)
  visibleTabs = visibleTabs.filter((t) => {
    const obj = normalizeTabRef(t?.object_name);
    const name = normalizeTabRef(t?.name);
    if (obj === 'user' || name === 'user') return false;
    if (obj === 'mail_config' || name === 'mail_config' || name === 'mailconfig') return false;
    if (obj === 'email_template' || name === 'email_template' || name === 'emailtemplate') return false;
    return true;
  });
  if (selectedApp?.config && (selectedApp.config.tabs?.length > 0 || selectedApp.config.tabIds?.length > 0)) {
    visibleTabs = visibleTabs.filter((t) => {
      const pkg = getTabPackage(t);
      return !pkg || enabledPackages.includes(pkg);
    });
  }
  if (allowedIdSet && allowedIdSet.size > 0) {
    visibleTabs = visibleTabs.filter((t) => {
      if (t?.is_standard === false) return true;
      return t?.standard_tab_id && allowedIdSet.has(String(t.standard_tab_id));
    });
  } else if (allowedNameSet && allowedNameSet.size > 0) {
    visibleTabs = visibleTabs.filter((t) => {
      if (t?.is_standard === false) return true;
      const name = normalizeName(t?.name);
      const label = normalizeName(t?.label);
      const obj = normalizeName(t?.object_name);
      return (name && allowedNameSet.has(name)) || (label && allowedNameSet.has(label)) || (obj && allowedNameSet.has(obj));
    });
  }

  // Build bar order: use app config.slotOrder when present (so group can sit between tabs), else order by order_no then group.
  const slotOrder = selectedApp?.config?.slotOrder;
  let barItems;
  if (Array.isArray(slotOrder) && slotOrder.length > 0 && selectedApp?.config?.tabs?.length > 0) {
    barItems = [];
    slotOrder.forEach((slot) => {
      if (slot?.type === 'tab') {
        const tab = visibleTabs.find((t) =>
          (slot.name != null && (t?.name || '').toString().toLowerCase().trim() === (slot.name || '').toString().toLowerCase().trim())
          || (slot.id != null && String(t?.id) === String(slot.id))
        );
        if (tab) barItems.push({ type: 'tab', tab });
      } else if (slot?.type === 'group' && slot.group_key) {
        const gk = (slot.group_key || '').trim();
        const groupTabs = visibleTabs.filter((t) => getGroupKey(t) === gk).sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
        if (groupTabs.length > 0) {
          const label = getGroupLabel(groupTabs[0]) || titleCase(gk);
          barItems.push({ type: 'group', id: `api-${gk}`, key: gk, label, tabs: groupTabs });
        }
      }
    });
  } else {
    const tabsByOrder = [...visibleTabs].sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
    barItems = [];
    const groupIdsAdded = new Set();
    tabsByOrder.forEach((tab) => {
      const gk = getGroupKey(tab);
      if (!gk) {
        barItems.push({ type: 'tab', tab });
        return;
      }
      if (groupIdsAdded.has(gk)) return;
      groupIdsAdded.add(gk);
      const groupTabs = visibleTabs.filter((t) => getGroupKey(t) === gk).sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
      const label = getGroupLabel(groupTabs[0]) || titleCase(gk);
      barItems.push({ type: 'group', id: `api-${gk}`, key: gk, label, tabs: groupTabs });
    });
  }

  // Split bar items into visible (first 10) and "More" dropdown; only top-level tabs go to More, groups stay as single items
  const maxVisible = 10;
  const visibleBarItems = barItems.slice(0, maxVisible);
  const moreBarItems = barItems.slice(maxVisible);
  const routeActiveTab = visibleTabs.find((tab) => isTabActive(tab));
  const activeInVisible = routeActiveTab && visibleBarItems.some((item) =>
    item.type === 'tab' ? item.tab.id === routeActiveTab.id : item.tabs.some((t) => t.id === routeActiveTab.id)
  );
  const activeBarItem = routeActiveTab && barItems.find((item) =>
    item.type === 'tab' ? item.tab.id === routeActiveTab.id : item.tabs.some((t) => t.id === routeActiveTab.id)
  );
  const supplementalBarItems = !activeInVisible && activeBarItem ? [activeBarItem] : [];
  const orderedVisibleBarItems =
    supplementalBarItems.length > 0
      ? [...visibleBarItems.filter((item) => item !== activeBarItem), ...supplementalBarItems]
      : visibleBarItems;
  const dropdownBarItems = moreBarItems.filter((item) => item !== activeBarItem);

  const handleCreateRecord = tab => {
    const objectName = tab.object_name?.toLowerCase();
    if (!objectName) return;
    navigate(`/edit/${objectName}`);
  };

  return (
    <>
      {/* Render bar: top-level tabs and group dropdowns in one order (by order_no) */}
      {orderedVisibleBarItems.map((barItem) => {
        if (barItem.type === 'group') {
          const group = barItem;
          const groupActive = group.tabs.some((tab) => isTabActive(tab));
          return (
            <Dropdown key={group.id}>
              <Dropdown.Toggle
                variant="link"
                className="menu-group-toggle"
                style={{
                  color: groupActive ? '#0d47a1' : '#1a1a1a',
                  border: 'none',
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  margin: '0',
                  padding: '10px 16px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: groupActive ? '600' : '500',
                  borderBottom: groupActive ? '3px solid #0d47a1' : '3px solid transparent',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0d47a1';
                  e.currentTarget.style.borderBottom = '3px solid #bbdefb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = groupActive ? '#0d47a1' : '#1a1a1a';
                  e.currentTarget.style.borderBottom = groupActive ? '3px solid #0d47a1' : '3px solid transparent';
                }}
              >
                {group.label} <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                align="end"
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  minWidth: '200px',
                  padding: '8px 0',
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}
              >
                {group.tabs.map((tab) => (
                  <Dropdown.Item
                    key={tab.id}
                    as={NavLink}
                    to={tab.url || '#'}
                    style={{
                      color: isTabActive(tab) ? theme.colors.primary : '#333',
                      fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                      padding: '8px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      backgroundColor: isTabActive(tab) ? '#f8f9fa' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = theme.colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = isTabActive(tab) ? '#f8f9fa' : 'transparent';
                      e.target.style.color = isTabActive(tab) ? theme.colors.primary : '#333';
                    }}
                  >
                    {tab.label || tab.name?.replace(new RegExp(group.key, 'gi'), '').replace(/_/g, ' ').trim() || tab.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          );
        }
        const tab = barItem.tab;
        const renderTabLink = () => (
          <NavLink
            to={tab.url || '#'}
            className='nav-link menu-nav-link'
            style={({ isActive }) => {
              const active = isActive || isTabActive(tab);
              return {
                color: active ? '#0d47a1' : '#1a1a1a',
                fontWeight: active ? '600' : '500',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 0,
                margin: '0',
                padding: '10px 16px',
                transition: 'all 0.2s ease',
                borderBottom: active
                  ? '3px solid #0d47a1'
                  : '3px solid transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              };
            }}
            onMouseEnter={e => {
              if (!isTabActive(tab)) {
                e.currentTarget.style.color = '#0d47a1';
                e.currentTarget.style.borderBottom = '3px solid #bbdefb';
              }
            }}
            onMouseLeave={e => {
              const active = isTabActive(tab);
              e.currentTarget.style.color = active ? '#0d47a1' : '#1a1a1a';
              e.currentTarget.style.borderBottom = active
                ? '3px solid #0d47a1'
                : '3px solid transparent';
            }}
          >
            {tab.label}
          </NavLink>
        );

        if (!tab.object_name) {
          return (
            <div
              key={tab.id}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {renderTabLink()}
            </div>
          );
        }

        return (
          <div
            key={tab.id}
            data-tab-dropdown-id={tab.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <div onClick={(e) => e.stopPropagation()}>
              {renderTabLink()}
            </div>
            <Dropdown
              align='end'
              show={openDropdownId === tab.id}
              onToggle={(isOpen) => {
                if (isOpen) {
                  setOpenDropdownId(tab.id);
                } else {
                  setOpenDropdownId(null);
                }
              }}
            >
              <Dropdown.Toggle
                variant='link'
                size='sm'
                className='tab-dropdown-toggle'
                style={{
                  color: '#1a1a1a',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '10px 6px',
                  marginLeft: '-8px',
                  borderBottom: '3px solid transparent',
                }}
                onClick={(e) => {
                  // Stop propagation to prevent tab navigation when clicking chevron
                  e.stopPropagation();
                  e.preventDefault();
                  setOpenDropdownId(openDropdownId === tab.id ? null : tab.id);
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = theme.colors.primary;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#1a1a1a';
                }}
              >
                <FaChevronDown size={10} />
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                  minWidth: 'auto',
                  width: 'max-content',
                  zIndex: 10060,
                  padding: '4px 0',
                }}
              >
                <Dropdown.Item
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenDropdownId(null);
                    handleCreateRecord(tab);
                  }}
                  className='d-flex align-items-center'
                  style={{
                    padding: '6px 12px',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FaPlus size={12} style={{ flexShrink: 0 }} />
                  <span>New {tab.label || tab.object_name}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        );
      })}

      {/* More: remaining bar items (tabs and groups) */}
      {dropdownBarItems.length > 0 && (
        <Dropdown className="menu-more-dropdown">
          <Dropdown.Toggle
            variant='link'
            style={{
              color: '#1a1a1a',
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: 0,
              margin: '0',
              padding: '10px 16px',
              transition: 'all 0.2s ease',
              fontSize: '14px',
              fontWeight: '500',
              borderBottom: '3px solid transparent',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
            onMouseEnter={e => {
              e.target.style.color = '#0d47a1';
              e.target.style.borderBottom = '3px solid #bbdefb';
            }}
            onMouseLeave={e => {
              e.target.style.color = '#1a1a1a';
              e.target.style.borderBottom = '3px solid transparent';
            }}
          >
            More ({dropdownBarItems.length}) <FaChevronDown size={10} style={{ marginLeft: '4px' }} />
          </Dropdown.Toggle>

          <Dropdown.Menu
            className="menu-more-dropdown-menu"
            align='end'
            style={{
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '200px',
              padding: '8px 0',
              maxHeight: '400px',
              overflowY: 'auto',
              overflowX: 'visible',
            }}
          >
            {dropdownBarItems.map((barItem) => {
              if (barItem.type === 'tab') {
                const tab = barItem.tab;
                return (
                  <Dropdown.Item
                    key={tab.id}
                    as={NavLink}
                    to={tab.url || '#'}
                    style={{
                      color: isTabActive(tab) ? theme.colors.primary : '#333',
                      fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                      padding: '8px 16px',
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      backgroundColor: isTabActive(tab) ? '#f8f9fa' : 'transparent',
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = '#f8f9fa'; e.target.style.color = theme.colors.primary; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = isTabActive(tab) ? '#f8f9fa' : 'transparent'; e.target.style.color = isTabActive(tab) ? theme.colors.primary : '#333'; }}
                  >
                    {tab.label}
                  </Dropdown.Item>
                );
              }
              const group = barItem;
              const isHovered = moreHoveredGroupKey === group.key;
              return (
                <div
                  key={`group-${group.key}`}
                  className="menu-more-group-item"
                  onMouseEnter={() => setMoreHoveredGroupKey(group.key)}
                  onMouseLeave={() => setMoreHoveredGroupKey(null)}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
                    color: '#333',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{group.label}</span>
                  <FaChevronRight size={10} className="ms-2" style={{ flexShrink: 0 }} />
                  {isHovered && (
                    <div
                      className="menu-more-flyout"
                      onMouseEnter={() => setMoreHoveredGroupKey(group.key)}
                      onMouseLeave={() => setMoreHoveredGroupKey(null)}
                      style={{
                        position: 'absolute',
                        left: '100%',
                        top: 0,
                        minWidth: '180px',
                        width: 'max-content',
                        overflow: 'visible',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '6px 0',
                        zIndex: 10070,
                        marginLeft: '2px',
                      }}
                    >
                      {group.tabs.map((tab) => (
                        <NavLink
                          key={tab.id}
                          to={tab.url || '#'}
                          className="dropdown-item"
                          style={{
                            display: 'block',
                            padding: '8px 16px',
                            fontSize: '14px',
                            color: isTabActive(tab) ? theme.colors.primary : '#333',
                            fontWeight: isTabActive(tab) ? 'bold' : 'normal',
                            textDecoration: 'none',
                            backgroundColor: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f8f9fa'; e.currentTarget.style.color = theme.colors.primary; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = isTabActive(tab) ? theme.colors.primary : '#333'; }}
                        >
                          {tab.label || tab.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </>
  );
};

export default Menu;
